import { BindingEngine, computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { type CompRightStoreProductModel, RecordType, RecordTypeEnum } from "../../../../../../models/rightstore/rightstore.models";
import { RuleCollectionRightStoreSetupModel, SaveRuleCollectionRightStoreSetupRequest } from "../../../../../../models/rules/rules.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RulesClientService } from "../../../../../../services/rules/rules-client.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { MultiSelectLookupControl } from "../../../../../components/lookups/multi-select-lookup-control/multi-select-lookup-control";

@autoinject()
@transient()
export class AddRuleCollectionRightstoreSetupDialog {
    private _uow: UnitOfWorkObserver<RuleCollectionRightStoreSetupModel>;

    ruleCollectionId: number;
    rightStoreSetupId: number;
    ruleCollectionName: string;
    rightStoreSetupModel: RuleCollectionRightStoreSetupModel;
    onSaved: () => void;
    tagsLookup: MultiSelectLookupControl;

    @computedFrom("rightStoreSetupModel", "rightStoreSetupModel.tags", "rightStoreSetupModel.tags.length")
    get selectedTags() {
        return this.rightStoreSetupModel && this.rightStoreSetupModel.tags ? this.rightStoreSetupModel.tags.map(x => x.rightStoreSetupTagId) : [];
    }

    @computedFrom("ruleCollectionId",
        "ruleCollectionName", "rightStoreSetupModel",
        "tagsLookup.selectedItems", "tagsLookup.selectedItems.length",
        "rightStoreSetupModel.description", 
        "rightStoreSetupModel.rightStoreProductUrl", "rightStoreSetupModel.imageUrl")
    get productModel(): CompRightStoreProductModel {
        return {
            name: this.ruleCollectionName,
            description: this.rightStoreSetupModel.description,
            tags: this.tagsLookup.selectedItems.map(x => x.name),
            url: this.rightStoreSetupModel.rightStoreProductUrl,
            imageUrl: this.rightStoreSetupModel.imageUrl,
            isComingSoon: false,
            expiry: null,
            showInPlatform: true
        };
    }

    public recordType?: number;
    public recordTypes: RecordType[] = [
        {
            recordType: null, 
            name: ""
        },
        {
            recordType: RecordTypeEnum.OneoffQuote, 
            name: "One-off Quote"
        },
        {
            recordType: RecordTypeEnum.Contract, 
            name: "Contract"
        },
    ];

    constructor(private dialogClientService: DialogClientService,
        private notificationService: NotificationClientService,
        private rulesService: RulesClientService,
        private validationsClientService: ValidationsClientService,
        private bindingEngine: BindingEngine,
        private validationController: ValidationController) {
        this.validationController.addRenderer(new CustomValidationRenderer())
    }

    setRules() {
        ValidationRules
            .ensure<RuleCollectionRightStoreSetupModel, any>(x => x.description)
            .required()
            .withMessage("Description is required")
            .satisfies(x => x && x.length <= 100)
            .withMessage("Description maximum of 100 characters")

            .ensure(x => x.rightStoreProductCode)
            .required()
            .withMessage("Optimizely product code is required")

            .ensure(x => x.rightStoreProductUrl)
            .required()
            .withMessage("Target URL is required")

            .ensure(x => x.imageUrl)
            .required()
            .withMessage("Image URL is required")

            .ensure(x => x.tags)
            .satisfies(x => x && x.length > 0)
            .withMessage("At least 1 tag is required")
            .on(this.rightStoreSetupModel);
    }

    async activate(model: { ruleCollectionId: number, rightStoreSetupId: number, ruleCollectionName: string, onSaved: () => void }) {
        this.ruleCollectionId = model.ruleCollectionId;
        this.rightStoreSetupId = model.rightStoreSetupId;
        this.ruleCollectionName = model.ruleCollectionName;

        this.onSaved = model.onSaved;

        if (this.rightStoreSetupId > 0) {
            this.rightStoreSetupModel = await this.getRightStoreSetup();

            this._uow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.rightStoreSetupModel, []);
        }
        else {
            this.initializeRightStoreSetupModel();
        }

        this.setRules();
    }

    initializeRightStoreSetupModel(): RuleCollectionRightStoreSetupModel {
        const model: RuleCollectionRightStoreSetupModel =
        {
            ruleCollectionRightStoreSetupId: null,
            ruleCollectionId: null,
            description: null,
            tags: null,
            rightStoreProductCode: null,
            rightStoreProductUrl: null,
            imageUrl: null,
            isActive: null,
            isInternal: null,
            recordType: null
        };

        this.rightStoreSetupModel = model;

        this._uow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.rightStoreSetupModel, []);

        //set inital values when new setup
        this.rightStoreSetupModel.ruleCollectionId = this.ruleCollectionId;
        this.rightStoreSetupModel.tags = [];
        this.rightStoreSetupModel.isActive = false;
        this.rightStoreSetupModel.isInternal = false;

        return this.rightStoreSetupModel;
    }

    async getRightStoreSetup(): Promise<RuleCollectionRightStoreSetupModel> {
        const response = await this.rulesService.getRuleCollectionRightStoreSetup(this.rightStoreSetupId);

        return response ? response.rightStoreSetup : null;
    }

    tagsChanged(event) {
        if (event && event.detail) {
            const selectedTagIds: number[] = event.detail.filter(x => x.isSelected).map(x => x.id);

            const tagsToRemove = this.rightStoreSetupModel.tags.filter(x => selectedTagIds.indexOf(x.rightStoreSetupTagId) == -1);

            for (const tag of tagsToRemove) {
                const index = this.rightStoreSetupModel.tags.findIndex(x => x.rightStoreSetupTagId == tag.rightStoreSetupTagId);
                if (index > -1) {
                    this.rightStoreSetupModel.tags.splice(index, 1);
                }
            }

            const tagsToAdd = selectedTagIds.filter(x => this.rightStoreSetupModel.tags.map(t => t.rightStoreSetupTagId).indexOf(x) == -1)
                .map(x => { return { rightStoreSetupTagId: x }; });

            if (tagsToAdd && tagsToAdd.length > 0)
                this.rightStoreSetupModel.tags.push(...tagsToAdd);
        }
    }

    close() {
        this.dialogClientService.hide();
    }

    async save() {
        try {
            if (!this._uow.hasChanges(true)) {
                this.notificationService.show(`No Changes`, "", NotificationType.Info);
                this.dialogClientService.hide();
                return;
            }
            const validationResponse = await this.validationController.validate();
            if (!validationResponse.valid) return;
            const saveRequest: SaveRuleCollectionRightStoreSetupRequest =
            {
                id: this.rightStoreSetupModel.ruleCollectionRightStoreSetupId,
                changes: this._uow.getChanges()
            };
            await this.rulesService.saveRuleCollectionRightStoreSetup(saveRequest);
            this.notificationService.show(`RightStore Setup has been ${this.rightStoreSetupId > 0 ? 'saved' : 'added'} Successfully`, "", NotificationType.Success);
            if (this.onSaved)
                this.onSaved();
            this.dialogClientService.hide();
        }
        catch (e) {
            if (e && e.content && e.content.validationResult && e.content.validationResult.hasError) {
                this.validationsClientService.showValidationErrors(e.content.validationResult);
            }
            else this.notificationService.show(`Failed to ${this.rightStoreSetupId > 0 ? 'save' : 'add'} RightStore Setup`, "", NotificationType.Error);
            throw e;
        }
    }

    public detached() {
        if (this._uow) {
            this._uow.dispose();
            this._uow = null;
        }
    }

}